import { useState } from "react";

import { supabaseClient } from "@/config/supabase";

interface UseGoogleOAuthReturn {
  handleGoogleSignIn: () => void;
  handleGoogleSignUp: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for Google OAuth with Supabase (web).
 *
 * Uses Supabase's native OAuth redirect flow (same as mobile app uses native SDK).
 * This properly handles the Google OAuth handshake and returns a valid ID token.
 *
 * Flow:
 * 1. User clicks "Sign in/Sign up with Google" button
 * 2. Redirects to Supabase OAuth endpoint (handles Google OAuth)
 * 3. Google signs them in and Supabase redirects back to /auth/callback
 * 4. Session is established automatically by Supabase
 * 5. User is logged in or account is created in omnibus
 */
export const useGoogleOAuth = (): UseGoogleOAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initiate Google OAuth via Supabase redirect flow.
   * Redirects to Supabase auth endpoint which handles the Google OAuth handshake.
   * Uses `prompt: 'select_account'` to always show Google account picker.
   */
  const initiateGoogleOAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { error: oauthError } = await supabaseClient.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            prompt: "select_account", // Always show account picker
          },
        },
      });

      if (oauthError) {
        console.error("[useGoogleOAuth] OAuth error:", oauthError);
        setError(oauthError.message);
        setIsLoading(false);
        return;
      }

      // Redirect will happen automatically - don't set loading false
    } catch (err) {
      console.error("[useGoogleOAuth] Unexpected error:", err);
      setError("An unexpected error occurred");
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    initiateGoogleOAuth();
  };

  const handleGoogleSignUp = () => {
    initiateGoogleOAuth();
  };

  return {
    handleGoogleSignIn,
    handleGoogleSignUp,
    isLoading,
    error,
  };
};
